/**
 * Fuzzy matching for the column search boxes (the combobox pickers and the
 * dual-pane selector). Replaces plain substring matching so a sloppy query like
 * "proplsion power" still finds `propulsion_power`: a missing character, and a
 * space where the name has a `_`.
 *
 * The query is split on whitespace into tokens; EVERY token must match the text
 * as a subsequence, but tokens are order-independent - both "power propulsion"
 * and "propulsion power" find `propulsion_power`. Within a token, matching is a
 * classic fzf-style left-greedy subsequence walk, scored with bonuses for
 * consecutive runs and for landing on a word boundary (start of string or right
 * after a `_`/`-`/space), so the tightest, most boundary-aligned candidates rank
 * first. Pure (no DOM, no atoms) so it's trivially unit-testable.
 */

const CONSECUTIVE_BONUS = 8;
const BOUNDARY_BONUS = 10;
const MATCH_BONUS = 1;
const GAP_PENALTY = 1;

function isBoundary(prev: string | undefined): boolean {
  return prev === undefined || prev === '_' || prev === '-' || prev === ' ';
}

/**
 * Score one whitespace-free token against `text` (both already lowercased).
 * Returns a score (higher = better) or null when `token` isn't a subsequence of
 * `text`. Left-greedy matching decides subsequence membership exactly; the
 * scoring only affects ranking.
 */
function tokenScore(token: string, text: string): number | null {
  let score = 0;
  let from = 0;
  let prevMatch = -2;
  let firstIdx = -1;
  for (const ch of token) {
    const at = text.indexOf(ch, from);
    if (at === -1) return null;
    if (firstIdx === -1) firstIdx = at;
    if (at === prevMatch + 1) score += CONSECUTIVE_BONUS;
    else score -= Math.min(at - prevMatch - 1, 4) * GAP_PENALTY;
    if (isBoundary(text[at - 1])) score += BOUNDARY_BONUS;
    score += MATCH_BONUS;
    prevMatch = at;
    from = at + 1;
  }
  // Tie-break toward earlier first matches without overwhelming the bonuses.
  return score - firstIdx * 0.1;
}

/**
 * Fuzzy-match `query` against `text`. Returns a combined score (higher = better)
 * or null when any query token fails to match. An empty query matches everything
 * with a neutral score of 0, so callers can keep their natural ordering when the
 * box is blank.
 */
export function fuzzyScore(query: string, text: string): number | null {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const t = text.toLowerCase();
  let total = 0;
  for (const token of q.split(/\s+/)) {
    if (!token) continue;
    const s = tokenScore(token, t);
    if (s === null) return null;
    total += s;
  }
  return total;
}

/** Boolean convenience wrapper around {@link fuzzyScore}. */
export function fuzzyMatch(query: string, text: string): boolean {
  return fuzzyScore(query, text) !== null;
}
